import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Form, Row, Upload } from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    FileImageOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    UploadOutlined,
    SaveOutlined
} from '@ant-design/icons';
import { t } from 'i18next';
import HTTPService from '../../../../utils/makerequest';
import { Toast } from 'primereact/toast';

interface FileTabProps {
    inventoryId: number;
}

const { Dragger } = Upload;

const FileTab: React.FC<FileTabProps> = ({ inventoryId }) => {
    const [fileList, setFileList] = useState<any[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const toast = useRef<Toast>(null);

    useEffect(() => {
        if (inventoryId !== 0) {
            fetchFiles(inventoryId);
        }
    }, [inventoryId]);

    const fetchFiles = (inventoryId: number) => {
        HTTPService.GET(`/Maintenance/DocumentsById?inventoryId=${inventoryId}`)
            .then(res => {
                if (res.status === 200 && res.data) {
                    setFileList(
                        res.data.map((file: any) => ({
                            uid: file.pictureId,
                            name: file.fileName,
                            status: 'done',
                            //url: file.url,
                            pictureId: file.pictureId,
                            type: file.type,
                            icon: getFileIcon(file.type)
                        }))
                    );
                } else {
                    toast.current?.show({ severity: 'error', summary: t('errors'), detail: t('error_fetching_data'), life: 3000 });
                }
            })
            .catch(() => {
                toast.current?.show({ severity: 'error', summary: t('errors'), detail: t('error_fetching_data'), life: 3000 });
            });
    };

    const handleChange = (info: any) => {
        setFileList(info.fileList);
    };

    const beforeUpload = (file: any) => {
        const allowed = [
            'image/jpeg', 'image/png',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/pdf'
        ];
        if (!allowed.includes(file.type)) {
            toast.current?.show({ severity: 'warn', summary: t('Warning'), detail: 'Sadece JPG, PNG, DOC, DOCX ve PDF dosyalarına izin verilmektedir.', life: 3000 });
        }
        return false;
    };

    const getFileIcon = (fileType?: string) => {
        if (!fileType) return <DownloadOutlined style={{ color: '#8c8c8c', fontSize: 20 }} />;
        const type = fileType.toLowerCase();
        if (type.includes('image/')) return <FileImageOutlined style={{ color: '#1890ff', fontSize: 20 }} />;
        if (type.includes('pdf')) return <FilePdfOutlined style={{ color: 'red', fontSize: 20 }} />;
        if (type.includes('word')) return <FileWordOutlined style={{ color: 'blue', fontSize: 20 }} />;
        return <DownloadOutlined style={{ color: '#8c8c8c', fontSize: 20 }} />;
    };

    const handleSave = async () => {
        for (const file of fileList) {
            if (file.status === 'done') continue;
            const form = new FormData();
            form.append('files', file.originFileObj);
            form.append('inventoryId', inventoryId.toString());
            try {
                const res = await HTTPService.POST('/Maintenance/uploadFiles', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                if (res.status === 200) {
                    toast.current?.show({ severity: 'success', summary: t('success'), detail: `${file.name} ${t('file_upload_success')}`, life: 3000 });
                    window.location.replace('/admin/inventory-management');
                } else throw new Error();
            } catch {
                toast.current?.show({ severity: 'error', summary: t('errors'), detail: `${file.name} ${t('file_upload_error')}`, life: 3000 });
            }
        }
    };

    const handleDelete = async (file: any) => {
        try {
            const res = await HTTPService.DELETE(`/Maintenance/deleteMaintenanceFile?pictureId=${file.pictureId}`);
            if (res.status === 200) {
                setFileList(prev => prev.filter(f => f.uid !== file.uid));
                toast.current?.show({ severity: 'success', summary: t('success'), detail: `${file.name} ${t('file_delete_success')}`, life: 3000 });
            } else throw new Error();
        } catch {
            toast.current?.show({ severity: 'error', summary: t('errors'), detail: `${file.name} ${t('file_delete_error')}`, life: 3000 });
        }
    };

    const handleDownload = async (file: any) => {
        if (isDownloading) return;

        try {
            setIsDownloading(true);
            const res = await HTTPService.POSTblob(`/Maintenance/downloadPicture?pictureId=${file.pictureId}`, null);
            if (res.status === 200 && res.data) {
                const blob = new Blob([res.data], { type: res.headers['content-type'] });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = file.name;
                document.body.appendChild(a);
                //a.click();
                URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.current?.show({ severity: 'success', summary: t('success'), detail: t('download_success'), life: 3000 });
            } else throw new Error();
        } catch {
            toast.current?.show({ severity: 'error', summary: t('errors'), detail: t('download_failed'), life: 3000 });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div>
            <Toast ref={toast} />
            <Dragger
                style={{ marginBottom: 20 }}
                fileList={fileList}
                onChange={handleChange}
                beforeUpload={beforeUpload}
                iconRender={file => getFileIcon(file.type)}

                previewFile={() => Promise.resolve('')} 

                onPreview={() => { }}

                showUploadList={{
                    showRemoveIcon: true,
                    showDownloadIcon: false,   
                    showPreviewIcon: false   
                }}
                itemRender={(originNode, file) => (
                    <div
                        onClick={e => e.stopPropagation()} 
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 8,
                            cursor: 'default'
                        }}
                    >
                        <div style={{ flex: 1 }}>{file.name}</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                                type="text"
                                disabled={isDownloading}
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDownload(file);
                                }}
                                style={{ fontSize: 20, color: '#1890ff', transition: 'all 0.2s ease' }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.transform = 'scale(1.2)';
                                    e.currentTarget.style.color = '#40a9ff';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.color = '#1890ff';
                                }}
                            >
                                <DownloadOutlined />
                            </Button>
                            <Button
                                type="text"
                                danger
                                onClick={() => handleDelete(file)}
                                style={{ fontSize: 20 }}
                            >
                                <DeleteOutlined />
                            </Button>
                        </div>
                    </div>
                )}
                multiple
            >
                <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                </p>
                <p className="ant-upload-text">Dosyayı buraya sürükleyin veya tıklayarak seçin</p>
                <p className="ant-upload-hint">JPG, PNG, DOC ve PDF dosyalarına izin verilmektedir.</p>
            </Dragger>



            <Form.Item style={{ marginTop: 25 }}>
                <Row justify="end">
                    <Col xs={12} sm={12} md={4}>
                        <Button
                            block
                            onClick={handleSave}
                            type="primary"
                            style={{ height: 64, backgroundColor: 'transparent', color: '#1890ff', border: 'none', transition: 'all 0.3s ease', cursor: 'pointer' }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                                e.currentTarget.style.color = '#40a9ff';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.color = '#1890ff';
                            }}
                        >
                            <SaveOutlined style={{ fontSize: 20 }} />
                            <div style={{ fontSize: 12, marginTop: 8 }}>{t('save')}</div>
                        </Button>
                    </Col>
                </Row>
            </Form.Item>
        </div>
    );
};

export default FileTab;
